import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, TextField, Grid, Card, CardContent, Chip, 
  IconButton, Tabs, Tab, InputAdornment, Avatar, AvatarGroup,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, 
  Select, MenuItem, OutlinedInput, Checkbox, ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, assigned_me, unassigned
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [types, setTypes] = useState([]);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    status: 'Open',
    priority: 'Medium',
    requested_by: '',
    sender_email: '',
    project_id: '',
    ticket_type_id: '',
    assigned_to_ids: [],
    due_date: ''
  });
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = { user_id: userId, filter };
      const res = await axios.get(`${API_URL}/tickets`, { headers, params });
      setTickets(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMeta = async () => {
      try {
        const [usersRes, projectsRes, typesRes] = await Promise.all([
          axios.get(`${API_URL}/team`, { headers }),
          axios.get(`${API_URL}/projects`, { headers }),
          axios.get(`${API_URL}/ticket-types`, { headers })
        ]);
        setUsers(usersRes.data);
        setProjects(projectsRes.data);
        setTypes(typesRes.data);
      } catch (err) {
        console.error(err);
      }
  };

  useEffect(() => {
    fetchTickets();
    fetchMeta();
  }, [filter]);

  const handleCreate = async () => {
    setCreateLoading(true);
    try {
      const payload = { ...newTicket, created_by_id: userId };
      await axios.post(`${API_URL}/tickets`, payload, { headers });
      setOpen(false);
      fetchTickets();
      setNewTicket({
        title: '', description: '', status: 'Open', priority: 'Medium',
        requested_by: '', sender_email: '', project_id: '', ticket_type_id: '',
        assigned_to_ids: [], due_date: ''
      });
    } catch (err) {
      alert('Error creating ticket');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setFilter(newValue);
  };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Done': return 'success';
          case 'In Progress': return 'info';
          case 'Open': return 'warning';
          default: return 'default';
      }
  };

  const getPriorityColor = (priority) => {
      switch(priority) {
          case 'Critical': return 'error';
          case 'High': return 'warning';
          case 'Medium': return 'info';
          case 'Low': return 'success';
          default: return 'default';
      }
  };

  const filteredTickets = tickets.filter(t => 
    t.title.toLowerCase().includes(search.toLowerCase()) || 
    (t.type && t.type.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Tabs value={filter} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
            <Tab label="All Tickets" value="all" />
            <Tab label="Assigned to Me" value="assigned_me" />
            <Tab label="Unassigned" value="unassigned" />
            <Tab label="Closed" value="closed" />
        </Tabs>
      
        <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField 
                placeholder="Search tickets..." 
                size="small" 
                value={search} 
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    sx: { borderRadius: 5, bgcolor: 'white' }
                }}
            />
            <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => setOpen(true)}
                sx={{ borderRadius: 5, px: 3 }}
            >
                Create Ticket
            </Button>
        </Box>
      </Box>

      {loading ? <Loading /> : (
        <Grid container spacing={3}>
            {filteredTickets.length === 0 ? (
                <Grid item xs={12}>
                    <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>No tickets found</Typography>
                </Grid>
            ) : filteredTickets.map((t) => (
                <Grid item xs={12} md={4} lg={3} key={t.id}>
                    <Card 
                        sx={{ height: '100%', borderRadius: 3, boxShadow: 1, cursor: 'pointer', transition: '0.3s', '&:hover': { boxShadow: 4 } }} 
                        onClick={() => navigate(`/tickets/${t.id}`)}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ overflow: 'hidden', mr: 1 }}>
                                    <Typography variant="caption" color="primary" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                                        {`ACTS - T - ${String(t.id).padStart(5, '0')}`}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{t.type}</Typography>
                                    {t.requested_by && <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Req: {t.requested_by}</Typography>}
                                </Box>
                                <Chip label={t.status} size="small" color={getStatusColor(t.status)} sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }} />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="textSecondary">{t.created_at}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="textSecondary">Due: {t.due_date || 'N/A'}</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(t.priority) }} />
                                    <Typography variant="caption" color="textSecondary">{t.priority || 'Medium'}</Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #eee' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="textSecondary">By: {t.created_by}</Typography>
                                </Box>
                                
                                {t.assigned_to && t.assigned_to.length > 0 ? (
                                    <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}>
                                        {t.assigned_to.map((name, i) => (
                                            <Avatar key={i} alt={name} src="/static/images/avatar/1.jpg" />
                                        ))}
                                    </AvatarGroup>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Ticket</DialogTitle>
        <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField 
                    label="Title / Subject" 
                    fullWidth 
                    value={newTicket.title} 
                    onChange={e => setNewTicket({...newTicket, title: e.target.value})} 
                    required
                />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={newTicket.ticket_type_id}
                            label="Type"
                            onChange={e => setNewTicket({...newTicket, ticket_type_id: e.target.value})}
                        >
                            {types.map((t) => (
                                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                            value={newTicket.priority}
                            label="Priority"
                            onChange={e => setNewTicket({...newTicket, priority: e.target.value})}
                        >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Critical">Critical</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField 
                        fullWidth 
                        label="Requested By" 
                        value={newTicket.requested_by} 
                        onChange={e => setNewTicket({...newTicket, requested_by: e.target.value})} 
                    />
                    <TextField 
                        fullWidth 
                        label="Sender Email" 
                        value={newTicket.sender_email} 
                        onChange={e => setNewTicket({...newTicket, sender_email: e.target.value})} 
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Related Project</InputLabel>
                        <Select
                            value={newTicket.project_id}
                            label="Related Project"
                            onChange={e => setNewTicket({...newTicket, project_id: e.target.value})}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {projects.map((p) => (
                                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField 
                        fullWidth 
                        type="date"
                        label="Due Date" 
                        value={newTicket.due_date} 
                        onChange={e => setNewTicket({...newTicket, due_date: e.target.value})} 
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                <FormControl fullWidth>
                    <InputLabel>Assign To (Team)</InputLabel>
                    <Select
                        multiple
                        value={newTicket.assigned_to_ids}
                        onChange={(e) => {
                            const { target: { value } } = e;
                            setNewTicket({
                                ...newTicket,
                                assigned_to_ids: typeof value === 'string' ? value.split(',') : value,
                            });
                        }}
                        input={<OutlinedInput label="Assign To (Team)" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => {
                                    const user = users.find(u => u.id === value);
                                    return <Chip key={value} label={user ? user.name : value} size="small" />;
                                })}
                            </Box>
                        )}
                        MenuProps={MenuProps}
                    >
                        {users.map((user) => (
                            <MenuItem key={user.id} value={user.id}>
                                <Checkbox checked={newTicket.assigned_to_ids.indexOf(user.id) > -1} />
                                <ListItemText primary={user.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField 
                    fullWidth 
                    multiline 
                    rows={4} 
                    label="Description" 
                    value={newTicket.description} 
                    onChange={e => setNewTicket({...newTicket, description: e.target.value})} 
                />
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} variant="contained" disabled={createLoading}>
              {createLoading ? 'Creating...' : 'Create Ticket'}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Tickets;
